import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  delay,
  EMPTY,
  expand,
  finalize,
  map,
  Observable,
  switchMap,
  takeWhile,
  tap,
  throwError,
  timer
} from 'rxjs';
import { RiskMitigationRisk } from 'src/app/models/crd-state.interface';
import { RiskMitigationPayload } from 'src/app/models/risk-mitigation-payload.model';
import { environment } from 'src/environments/environment';
import { EventStateService } from '../state-service/event-state.service';
import { formatDate } from '@angular/common';
import { UtilityService } from '../utility.service';
import { TranslocoService } from '@jsverse/transloco';
import {
  AgriTraceFile,
  JobStatus,
  PreviewUrlPayload,
  PreviewUrlResponse
} from 'src/app/models/risk-mitigation-v2.model';
import { AGRITRACE_PREVIEW_STATUS } from 'src/app/enums/agritrace-preview-status.enum';

@Injectable({
  providedIn: 'root'
})
export class RiskMitigationService {
  private readonly http = inject(HttpClient);
  private readonly eventStateService = inject(EventStateService);
  private readonly utilityService = inject(UtilityService);
  private readonly translocoService = inject(TranslocoService);

  getRiskDataByPlantationCode(
    plantationCodes: string[],
    selectedPeriod: string
  ): Observable<RiskMitigationRisk[]> {
    const headers = new HttpHeaders({
      'Accept-Language': this.utilityService.getLanguageHeaderValue(
        this.translocoService.getActiveLang()
      )
    });

    this.eventStateService.isRiskDataByPlantationCodeLoading = true;
    return this.http
      .post<
        RiskMitigationRisk[]
      >(`${environment.CRD_API}v2/plantations/mitigations/risks?date=${selectedPeriod}`, plantationCodes, { headers })
      .pipe(
        map((res) => {
          const updatedData: RiskMitigationRisk[] = res
            .filter(
              (risk) => Object.keys(risk).length > 0 && risk.needs_mitigation
            )
            .map((risk) => ({
              ...risk,
              updated_date: risk?.updated_date
                ? formatDate(risk.updated_date, 'dd/MM/yyyy', 'en-us')
                : null,
              statusChipProps: risk?.status
                ? this.utilityService.getMitigationStatusChipProps(risk.status)
                : null
            }));

          return updatedData;
        }),
        finalize(() => {
          this.eventStateService.isRiskDataByPlantationCodeLoading = false;
        })
      );
  }

  riskVerification(
    payload: RiskMitigationPayload,
    version = 'v3/plantations'
  ): Observable<{ message: string }> {
    return this.http
      .patch<{
        message: string;
      }>(`${environment.CRD_API}${version}/mitigation/risks`, payload)
      .pipe(
        delay(3000),
        tap(() => {
          this.eventStateService.refreshTable = true;
        })
      );
  }

  /**
   * Risk mitigation v2 (AgriTrace Files)
   */
  getAgriTracePlantationsFiles(
    plantation_doc_id: string
  ): Observable<AgriTraceFile[]> {
    return this.http
      .get<
        AgriTraceFile[]
      >(`${environment.CRD_API}v1/agtrace/plantation/files/?plantation_doc_id=${plantation_doc_id}`)
      .pipe(
        map((files) =>
          [...files].map((f) => ({
            ...f,
            isAgTrace: true
          }))
        )
      );
  }

  getAgriTraceFilePreview(payload: PreviewUrlPayload): Observable<JobStatus> {
    return this.getFilePreview(payload).pipe(
      switchMap((previewResponse) => {
        const jobId = previewResponse?.job_id || '';

        this.utilityService.emitToast({
          isSuccess: true,
          message: previewResponse?.message
        });

        if (!jobId) {
          return throwError(
            () => new Error('No job_id found in preview response')
          );
        }

        return this.getJobStatus(jobId).pipe(
          expand((status) => {
            if (
              status.error_message ||
              status.status === AGRITRACE_PREVIEW_STATUS.COMPLETED
            ) {
              return EMPTY;
            }

            return timer(2000).pipe(switchMap(() => this.getJobStatus(jobId)));
          }),
          tap((status) => {
            if (status?.error_message) {
              this.utilityService.emitToast({
                isSuccess: false,
                message: status.error_message
              });
            }
          }),
          takeWhile(
            (status) =>
              status?.status.toLowerCase() !==
                AGRITRACE_PREVIEW_STATUS.COMPLETED && !status?.error_message,
            true
          )
        );
      })
    );
  }

  private getFilePreview(
    payload: PreviewUrlPayload
  ): Observable<PreviewUrlResponse> {
    return this.http.post<PreviewUrlResponse>(
      `${environment.CRD_API}v1/agtrace/plantation/job/generate-preview-url/`,
      payload
    );
  }

  private getJobStatus(jobId: string): Observable<JobStatus> {
    return this.http.get<JobStatus>(
      `${environment.CRD_API}v1/agtrace/plantation/job/status/?job_id=${jobId}`
    );
  }
}
